import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addLieferantenrechnungPosition,
    LieferantenrechnungPosition,
    defaultLieferantenrechnungPosition,
    updateLieferantenrechnungPosition,
    useLieferantenrechnungPositionList,
} from 'api/lieferantenrechnungPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { getArtikelDetailLieferantList } from 'api/artikelDetailLieferant';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { Kunde } from 'api/kunde';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import useArtikelCalculator from 'hooks/useArtikelCalculator';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    addLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '6fr 1fr',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface LieferantenrechnungPositionEditProps {
    kunde: Kunde;
    rNr: string;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const LieferantenrechnungPositionEdit: React.FC<LieferantenrechnungPositionEditProps> = ({
    kunde,
    rNr,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();
    const artikelCalculator = useArtikelCalculator();

    const lieferantenrechnungPositionList = useLieferantenrechnungPositionList(
        {
            rNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [
        lieferantenrechnungPosition,
        setLieferantenrechnungPosition,
    ] = React.useState<LieferantenrechnungPosition | null>(null);

    React.useEffect(() => {
        if (
            !lieferantenrechnungPositionList.isLoading &&
            !lieferantenrechnungPositionList.isFetching &&
            lieferantenrechnungPosition === null
        ) {
            if (lieferantenrechnungPositionList.data?.length === 1) {
                setLieferantenrechnungPosition(cloneDeep(lieferantenrechnungPositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (
                    lieferantenrechnungPositionList.data[0].artikelGruppeNr &&
                    lieferantenrechnungPositionList.data[0].artikelNr
                ) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: lieferantenrechnungPositionList.data[0].artikelGruppeNr,
                        artikelNr: lieferantenrechnungPositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setLieferantenrechnungPosition({
                    ...defaultLieferantenrechnungPosition,
                    rNr,
                    pos,
                    posMwst: kunde.land === 'AT' ? 20 : 0,
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [lieferantenrechnungPosition, lieferantenrechnungPositionList, rNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !lieferantenrechnungPositionList.isLoading &&
            !lieferantenrechnungPositionList.isFetching &&
            lieferantenrechnungPositionList.data?.length === 1 &&
            lieferantenrechnungPosition !== null &&
            !isEqual(lieferantenrechnungPositionList.data[0], lieferantenrechnungPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference(
                'lieferantenrechnungPosition',
                lieferantenrechnungPosition,
                lieferantenrechnungPositionList.data[0],
            );
        }
    }, [savingState, lieferantenrechnungPositionList, lieferantenrechnungPosition]);

    React.useEffect(() => {
        if (lieferantenrechnungPosition) {
            if (lieferantenrechnungPosition.posMenge && lieferantenrechnungPosition.posEPreis) {
                const netto =
                    Math.round(lieferantenrechnungPosition.posMenge * lieferantenrechnungPosition.posEPreis * 100) /
                    100;
                const mwstFactor = lieferantenrechnungPosition.posMwst
                    ? 1 + lieferantenrechnungPosition?.posMwst / 100
                    : 1;
                const brutto = lieferantenrechnungPosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;

                setLieferantenrechnungPosition({
                    ...lieferantenrechnungPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setLieferantenrechnungPosition({
                    ...lieferantenrechnungPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        lieferantenrechnungPosition?.posMenge,
        lieferantenrechnungPosition?.posEPreis,
        lieferantenrechnungPosition?.posMwst,
    ]);

    const validate = (): boolean => {
        if (!lieferantenrechnungPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (lieferantenrechnungPosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (lieferantenrechnungPosition.pos === -1) {
                    addLieferantenrechnungPosition(lieferantenrechnungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateLieferantenrechnungPosition(lieferantenrechnungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (
        column: keyof LieferantenrechnungPosition,
        label: string,
        options: FormTextFieldOptions = {},
    ) => (
        <FormTextField
            value={(lieferantenrechnungPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferantenrechnungPosition) {
                    setLieferantenrechnungPosition({
                        ...lieferantenrechnungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof LieferantenrechnungPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(lieferantenrechnungPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferantenrechnungPosition) {
                    setLieferantenrechnungPosition({
                        ...lieferantenrechnungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (
        column: keyof LieferantenrechnungPosition,
        label: string,
        options: FormTextareaOptions = {},
    ) => (
        <FormTextarea
            value={lieferantenrechnungPosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferantenrechnungPosition) {
                    setLieferantenrechnungPosition({
                        ...lieferantenrechnungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={lieferantenrechnungPosition?.artikelGruppeNr}
            artikelNr={lieferantenrechnungPosition?.artikelNr}
            disabled={lieferantenrechnungPosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (lieferantenrechnungPosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        setLieferantenrechnungPosition({
                            ...lieferantenrechnungPosition,
                            artikelGruppeNr: defaultLieferantenrechnungPosition.artikelGruppeNr,
                            artikelNr: defaultLieferantenrechnungPosition.artikelNr,
                            posText: defaultLieferantenrechnungPosition.posText,
                            posEkPreis: defaultLieferantenrechnungPosition.posEkPreis,
                            posEPreis: defaultLieferantenrechnungPosition.posEPreis,
                            posEinh: defaultLieferantenrechnungPosition.posEinh,
                            lkz: defaultLieferantenrechnungPosition.lkz,
                            pd: defaultLieferantenrechnungPosition.pd,
                            pz: defaultLieferantenrechnungPosition.pz,
                            lNr: defaultLieferantenrechnungPosition.lNr,
                        });
                        return;
                    }

                    setArtikelDetailKalkulationen(null);

                    const newArtikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                    });

                    const artikelDetailLieferanten = (
                        (await getArtikelDetailLieferantList({
                            artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                            artikelNr: newArtikelDetail.artikelNr,
                        })) || []
                    ).sort((a, b) => a.vkGiltAb.localeCompare(b.vkGiltAb));

                    setArtikelDetailKalkulationen(newArtikelDetailKalkulationen);

                    const defaultKalkulation = newArtikelDetailKalkulationen.find(
                        (k) => k.eh === newArtikelDetail.artikel?.einheit,
                    );

                    const { value } = artikelCalculator.convertPreisByEinheit(
                        newArtikelDetail.artikel,
                        defaultKalkulation?.ek1 || 0,
                        defaultKalkulation?.ek1Eh,
                        newArtikelDetail.artikel.einheit,
                    );

                    // const ekPreis = defaultKalkulation?.ek1 || 0;
                    // const ePreis = defaultKalkulation?.ek1 || 0;

                    setLieferantenrechnungPosition({
                        ...lieferantenrechnungPosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel?.text || '',
                        posEkPreis: value,
                        posEPreis: value,
                        posEinh: newArtikelDetail.artikel?.einheit || '',
                        lkz: 0,
                        pd: newArtikelDetail.produktGruppeNr,
                        pz: newArtikelDetail.produzentNr,
                        lNr: artikelDetailLieferanten[0]?.lNr,
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof LieferantenrechnungPosition,
        ekPreisColumn: keyof LieferantenrechnungPosition,
        ePreisColumn: keyof LieferantenrechnungPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(lieferantenrechnungPosition?.[column] as string) || ''}
            menuPlacement="top"
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek1 || 0;
                    ePreis = kalkulation?.ek1 || 0;
                }

                if (lieferantenrechnungPosition) {
                    setLieferantenrechnungPosition({
                        ...lieferantenrechnungPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title="Lieferantenrechnungsposition"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1100}
            onDismiss={() => {
                onClose(false);
            }}
        >
            <form className={formStyles.form}>
                <div className={styles.artikelLine}>
                    <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                    <div>{renderArtikelDetailSelect()}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                </div>
                <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                <div className={styles.posLine}>
                    <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                    <div>{renderDecimalField('posMenge', 'Menge', { fixed: 3 })}</div>
                    {/* <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div> */}
                    <div>{renderDecimalField('posEPreis', 'VK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posNetto', 'EK-Netto', { disabled: true })}</div>
                    <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                    <div>{renderDecimalField('posBrutto', 'EK Brutto', { disabled: true })}</div>
                </div>

                <Timestamps
                    modifiedDate={lieferantenrechnungPosition?.updDat}
                    modifiedDecimalTime={lieferantenrechnungPosition?.updUhr}
                    modifiedUser={lieferantenrechnungPosition?.updUser}
                    showCreated={false}
                />

                {validationError && <div className={formStyles.error}>{validationError}</div>}
                {savingState === SavingState.error && <div className={formStyles.error}>Fehler beim Speichern.</div>}

                <DialogFooter>
                    {savingState === SavingState.saving ? (
                        <PrimaryButton text="Speichere..." disabled />
                    ) : (
                        <PrimaryButton
                            onClick={save}
                            text="Speichern"
                            disabled={
                                savingState === SavingState.notChanged || lieferantenrechnungPositionList.isLoading
                            }
                        />
                    )}
                </DialogFooter>
            </form>
            {(lieferantenrechnungPositionList.isLoading ||
                lieferantenrechnungPositionList.isFetching ||
                !lieferantenrechnungPosition) && <LoadingIndicator />}
        </Dialog>
    );
};
